/**
 * Small, faceted models for the realm chart. Geometry is authored once in SVG
 * definitions; each holding/host is a use element, so hundreds of towns do not
 * duplicate the mesh on every council repaint. The light comes from the upper
 * left, like the city renderer. These models never read or change simulation.
 */

export type MapArm = 'foot' | 'horse' | 'shot' | 'engine' | 'caster';
type Point = [number, number];
type Material = [string, string, string];

const STONE: Material = ['#aaa58d', '#7d7d6a', '#505a53'];
const TIMBER: Material = ['#a89b79', '#82745c', '#535345'];
const ROOF: Material = ['#a39270', '#7b7359', '#555748'];
const SLATE: Material = ['#89918a', '#666f66', '#424e49'];
const CLAY: Material = ['#ae9172', '#856e55', '#5c5547'];
const DARK: Material = ['#848974', '#626c5e', '#414e47'];

function number(n: number): string { return String(Math.round(n * 100) / 100); }
function polygon(points: Point[], fill: string): string {
  return `<path d="M${points.map((p) => p.map(number).join(' ')).join('L')}Z" fill="${fill}"/>`;
}
function iso(x: number, y: number, z = 0): Point { return [x - y, (x + y) * 0.44 - z]; }

/** Three visibly different planes, with an edge highlight on the roofline. */
function block(x: number, y: number, w: number, d: number, h: number,
  colors: Material = STONE, z = 0): string {
  const a = iso(x, y, z + h), b = iso(x + w, y, z + h);
  const c = iso(x + w, y + d, z + h), e = iso(x, y + d, z + h);
  return polygon([e, c, iso(x + w, y + d, z), iso(x, y + d, z)], colors[1]) +
    polygon([b, c, iso(x + w, y + d, z), iso(x + w, y, z)], colors[2]) +
    polygon([a, b, c, e], colors[0]);
}

function roof(x: number, y: number, w: number, d: number, h: number, peak: number, colors = ROOF): string {
  const a = iso(x, y, h), b = iso(x + w, y, h);
  const c = iso(x + w, y + d, h), e = iso(x, y + d, h);
  const ridge0 = iso(x + w * 0.5, y, h + peak), ridge1 = iso(x + w * 0.5, y + d, h + peak);
  return polygon([a, e, ridge1, ridge0], colors[0]) +
    polygon([e, c, ridge1], TIMBER[1]) +
    polygon([ridge0, ridge1, c, b], colors[2]) +
    polygon([ridge0, ridge1, iso(x + w * 0.76, y + d, h + peak * 0.49)], colors[1]);
}

function door(x: number, y: number, width: number, height: number): string {
  return polygon([iso(x, y), iso(x + width, y), iso(x + width, y, height), iso(x, y, height)], '#323c35');
}

function house(x: number, y: number, size = 1, variant = 0): string {
  const w = size, d = size * (variant % 2 ? 1.17 : 0.86), h = size * 0.7;
  const color = variant % 4 === 0 ? SLATE : variant % 4 === 1 ? CLAY : ROOF;
  const shadow = polygon([iso(x, y + d), iso(x + w, y + d), iso(x + w + 0.52, y + d + 0.18),
    iso(x + 0.48, y + d + 0.2)], '#17231e66');
  return shadow + block(x, y, w, d, h, variant % 3 === 0 ? STONE : TIMBER) +
    roof(x - 0.05, y - 0.05, w + 0.1, d + 0.1, h, size * 0.48, color) +
    door(x + w * 0.4, y + d + 0.005, w * 0.24, h * 0.58) +
    polygon([iso(x + w * 0.12, y + d + 0.01, h * 0.56), iso(x + w * 0.3, y + d + 0.01, h * 0.56),
      iso(x + w * 0.3, y + d + 0.01, h * 0.76), iso(x + w * 0.12, y + d + 0.01, h * 0.76)], '#bda276');
}

function tower(x: number, y: number, h = 1.9, pointed = false): string {
  const w = 0.7;
  let out = block(x, y, w, w, h);
  out += block(x - 0.09, y - 0.09, w + 0.18, w + 0.18, 0.22, STONE, h - 0.06);
  if (pointed) out += roof(x - 0.12, y - 0.12, w + 0.24, w + 0.24, h + 0.16, 0.62, SLATE);
  else {
    for (const [dx, dy] of [[0, 0], [0.5, 0], [0, 0.5], [0.5, 0.5]]) {
      out += block(x + dx - 0.06, y + dy - 0.06, 0.32, 0.32, 0.32, STONE, h + 0.1);
    }
  }
  out += polygon([iso(x + 0.27, y + w + 0.005, h * 0.45), iso(x + 0.4, y + w + 0.005, h * 0.45),
    iso(x + 0.4, y + w + 0.005, h * 0.74), iso(x + 0.27, y + w + 0.005, h * 0.74)], '#303d35');
  return out;
}

function battlement(x: number, y: number, length: number, east = false): string {
  let out = block(x, y, east ? 0.18 : length, east ? length : 0.18, 0.66, DARK);
  const count = Math.max(3, Math.round(length / 0.3));
  for (let i = 0; i < count; i++) {
    const at = (i / (count - 1)) * (length - 0.13);
    out += block(x + (east ? 0 : at), y + (east ? at : 0), 0.15, 0.15, 0.17, STONE, 0.65);
  }
  return out;
}

function settlement(fort: number): string {
  // Soil and worn approaches lie flush with the landscape. The broken edge
  // lets neighbouring trees and contour shadows run naturally into the town.
  const base = `<g stroke="none">` +
    polygon([[-3.35, 0.46], [-2.74, -0.05], [-2.08, -0.22], [-1.54, -0.83], [-0.61, -0.7],
      [0.03, -1.02], [0.86, -0.7], [1.57, -0.76], [2.48, -0.12], [2.98, 0.07],
      [3.22, 0.67], [2.72, 1.2], [2.23, 1.26], [1.53, 1.99], [0.56, 2.14],
      [0.04, 1.85], [-0.82, 2.02], [-1.2, 1.61], [-2.19, 1.59], [-2.4, 1.07], [-3.12, 1.05]], '#202a225e') +
    polygon([[-2.98, 0.35], [-1.9, -0.36], [-1.13, -0.3], [-0.52, -0.74], [0.59, -0.41],
      [1.47, -0.57], [2.67, 0.17], [2.8, 0.73], [1.61, 1.39], [0.9, 1.44], [0.13, 1.76],
      [-0.85, 1.4], [-1.92, 1.48], [-2.03, 0.85], [-2.81, 0.89]], '#7d79614a') +
    `<path d="M-2.65.49L-.91.14L.12.5L1.27 1.05L2.66 1.16M.12.5L.37 1.32L1.29 2.1M-.91.14L-.24-.56" fill="none" stroke="#b4a17b" stroke-width=".19" opacity=".3" stroke-linecap="round"/>` +
    polygon([[-1.42, 0.45], [-0.6, -0.41], [1.32, -0.01], [2.73, 1.06], [2.13, 1.52], [0.7, 1.27]], '#101c1b70') + '</g>';
  const pieces: Array<{ depth: number; body: string }> = [];
  const add = (x: number, y: number, size: number, variant: number) => {
    pieces.push({ depth: x + y + size, body: house(x, y, size, variant) });
  };
  // Narrow lanes, varied ridge lengths, and staggered eaves make a place with
  // inhabitants instead of one oversized model on a token's pedestal.
  const cottages = [
    [-1.88, -0.37, 0.48], [-1.56, 0.23, 0.52], [-1.66, 0.93, 0.48],
    [-0.98, 1.28, 0.54], [-0.28, 1.53, 0.44], [0.41, 1.52, 0.5],
    [1.17, 1.13, 0.47], [1.51, 0.44, 0.51], [1.33, -0.2, 0.45],
    [0.82, -1.38, 0.43], [0.19, -1.61, 0.51], [-0.59, -1.61, 0.46],
    [-1.38, -1.26, 0.47], [-2.1, 0.18, 0.37], [0.93, 1.78, 0.35],
    [-0.83, 2.07, 0.34], [1.93, 0.02, 0.35], [-1.91, -0.99, 0.32],
  ];
  for (let i = 0; i < cottages.length; i++) {
    if (fort === 0 && i % 2 === 1) continue;
    if (fort === 1 && i > 12) continue;
    const [x, y, size] = cottages[i];
    add(x, y, size, i);
  }
  let keep: string;
  if (fort <= 0) {
    keep = house(-0.57, -0.56, 0.94, 1) + house(0.26, 0.14, 0.6, 3);
  } else if (fort === 1) {
    keep = block(-0.61, -0.66, 1.19, 1.28, 1.33) +
      roof(-0.7, -0.75, 1.37, 1.46, 1.33, 0.62, SLATE) +
      door(-0.2, 0.63, 0.35, 0.63) + house(-0.86, 0.65, 0.48, 2);
  } else {
    keep = tower(-1.02, -1.03, fort >= 3 ? 1.9 : 1.42, fort >= 3) +
      battlement(-0.95, -0.95, 1.91) +
      tower(0.48, -1.02, 1.38, fort >= 3) +
      block(-0.48, -0.47, 0.99, 1.02, fort >= 3 ? 1.97 : 1.55) +
      roof(-0.56, -0.55, 1.15, 1.18, fort >= 3 ? 1.97 : 1.55, 0.59, SLATE) +
      battlement(0.93, -0.59, 1.51, true) +
      battlement(-0.93, 0.87, 0.76) + battlement(0.31, 0.87, 0.76) +
      tower(-1.06, 0.48, 1.18) + tower(0.49, 0.47, 1.17) +
      block(-0.21, 0.88, 0.55, 0.24, 0.81, STONE) + door(-0.11, 1.13, 0.3, 0.59);
  }
  pieces.push({ depth: 0.75, body: keep });
  pieces.sort((a, b) => a.depth - b.depth);
  return `<g stroke="#2a342c" stroke-width=".025" stroke-linejoin="round">${base}${pieces.map((p) => p.body).join('')}</g>`;
}

/** Materials remain earthy; the chart conveys ownership without a pin over every roof. */
export function lowPolySettlement(fort: number, tier = 4): string {
  const count = Math.max(0, Math.min(4, Math.floor((Math.max(1, tier) - 1) / 2)));
  const spots = [[-2.8, 0.39, 0.43], [2.77, 0.66, 0.45], [-1.94, 1.49, 0.37], [1.61, 1.85, 0.35]];
  return spots.slice(0, count).map(([x, y, s]) =>
    `<use href="#wm-poly-hut" transform="translate(${x} ${y}) scale(${s})"/>`).join('') +
    `<use href="#wm-poly-hold-${Math.max(0, Math.min(3, Math.floor(fort)))}"/>`;
}

function villageRoofs(level: number): string {
  const sites = [[-1.4,-.9],[-.45,-1.1],[.65,-.85],[-1.25,.25],[.9,.35],[-.75,1.2],[.3,1.2],[-1.9,.8],[1.6,.85]];
  return polygon([iso(-2.4,-1.6),iso(2.2,-1.6),iso(2.2,2),iso(-2.4,2)], '#80775844') +
    sites.slice(0, level + 2).map(([x,y],i) => house(x,y,.53,i+1)).join('') +
    house(-.3,-.05,.67,1);
}

function villageCurtain(level: number): string {
  if (level < 3) return '<path d="M-2.5-.2L.1-1.5L2.9-.15M-2.5.5L.1 1.9L2.9.55" fill="none" stroke="#a79870" stroke-width=".15" stroke-dasharray=".1 .1"/>';
  return battlement(-1.9,-1.55,3.7) + battlement(1.8,-1.55,3.5,true) +
    battlement(-1.9,-1.55,3.5,true) + battlement(-1.9,1.85,1.5) + battlement(.35,1.85,1.5) +
    (level >= 6 ? tower(-2,-1.65,1.15) + tower(1.4,1.45,.92) : '');
}

function villageWork(type: string): string {
  if (type === 'farming') return polygon([iso(-3,.9),iso(-1.8,.9),iso(-1.8,2.4),iso(-3,2.4)], '#9e9658') +
    [0,.25,.5,.75,1].map(y => polygon([iso(-3,1+y),iso(-1.8,1+y),iso(-1.8,1.09+y),iso(-3,1.09+y)],'#4f6542')).join('');
  if (type === 'woodcutting') return [0,.3,.6].map(y => block(-2.7,1+y,1.2,.22,.2,TIMBER)).join('');
  if (type === 'fishing') return polygon([iso(-3.2,.9),iso(-1.8,.9),iso(-1.8,2.3),iso(-3.2,2.3)], '#5f8890') + block(-2.7,1.2,1.2,.25,.2,TIMBER);
  const material:Material = type === 'gold_mining' ? ['#cbb571','#8e8053','#655c41'] : type === 'soul_shrine' ? ['#c3b2d1','#9887aa','#615675'] : type === 'iron_mining' ? ['#9aa1a2','#717a7f','#505b61'] : STONE;
  return block(-2.8,.8,.64,.68,.58,material) + block(-2.3,1.3,.75,.6,.4,material) + block(-3,1.6,.6,.5,.28,material);
}

const VILLAGE_TYPES = ['farming','woodcutting','stone_quarry','iron_mining','fishing','soul_shrine','gold_mining'];
const VILLAGE_DEFS = Array.from({length:7},(_,i) => `<g id="wm-village-${i+1}">${villageRoofs(i+1)}</g>`).join('') +
  [1,3,6].map(l => `<g id="wm-village-walls-${l}">${villageCurtain(l)}</g>`).join('') +
  VILLAGE_TYPES.map(t => `<g id="wm-village-work-${t}">${villageWork(t)}</g>`).join('');

/** A resource settlement keeps cottages and yards, even at its highest level. */
export function lowPolyVillage(level: number, type: string, fort = 0): string {
  const lv = Math.max(1,Math.min(7,Math.floor(level)));
  const wall = fort >= 2 || lv >= 6 ? 6 : fort >= 1 || lv >= 3 ? 3 : 1;
  return `<use href="#wm-village-${lv}"/><use href="#wm-village-walls-${wall}"/><use href="#wm-village-work-${VILLAGE_TYPES.includes(type) ? type : 'farming'}"/>`;
}

export function lowPolyVillagePreview(level: number, type: string, fort = 0): string {
  const lv = Math.max(1,Math.min(7,Math.floor(level)));
  const wall = fort >= 2 || lv >= 6 ? 6 : fort >= 1 || lv >= 3 ? 3 : 1;
  return `<svg class="wm-groundpic" viewBox="-5 -3.3 10 6.6" aria-hidden="true">${villageRoofs(lv)}${villageCurtain(wall)}${villageWork(type)}</svg>`;
}

const BOOTS = '<path d="M-2.6 1.5L-.4 1.8L-.8 5.4L-3.4 5.4ZM.2 1.8L2.1 1.5L3 5.4L.4 5.4Z" fill="#273b49"/>' +
  '<path d="M-2.6 1.5L-1.6 1.7L-1.9 4.7L-3.3 5.4L-2.8 3ZM.2 1.8L1.2 1.7L1.8 4.8L.4 5.4Z" fill="#739095"/>';
const HELMET = '<path d="M-2.9-6.1L-2.4-8.4L-.5-9.4L1.5-8.2L1.9-5.8L-.4-4.7Z" fill="#c6d6d5"/>' +
  '<path d="M-.5-9.4L1.5-8.2L1.9-5.8L-.4-4.7L-.5-7.2Z" fill="#617f90"/>' +
  '<path d="M-2.5-6.5L1.4-6.1L.8-5.3L-2.1-5.6Z" fill="#20323e"/>';

function soldier(arm: MapArm): string {
  const caster = arm === 'caster';
  let out = BOOTS + '<path d="M-3.2-4.5L-.6-5.3L2.3-4L2.6 1.5L-.5 2.8L-3.6 1.2Z" fill="currentColor"/>' +
    '<path d="M-3.2-4.5L-.6-5.3L-.5 2.8L-3.6 1.2Z" fill="#ffffff33"/>' +
    '<path d="M-.6-5.3L2.3-4L2.6 1.5L-.5 2.8Z" fill="#15233255"/>' +
    '<path d="M-3.2-4.5L-.6-5.3L2.3-4L1.1-1.2L-2.1-1.5Z" fill="#a0b5bb"/>' +
    '<path d="M-.6-5.3L2.3-4L1.1-1.2L-.4-1.4Z" fill="#526d81"/>';
  if (caster) {
    out += '<path d="M-3-1.2L1.9-.8L3.7 4.8L-.6 5.8L-4.5 4.2Z" fill="currentColor"/>' +
      '<path d="M-.6-.8L1.9-.8L3.7 4.8L-.6 5.8Z" fill="#14253866"/>' +
      '<path d="M-3-6L-.6-10.2L2.2-5.9L-.5-4.7Z" fill="currentColor"/>' +
      '<path d="M-.6-10.2L2.2-5.9L-.5-4.7Z" fill="#bfd2cf66"/>' +
      '<path d="M-1.6-6.1L.7-5.9L-.3-5Z" fill="#273845"/>' +
      '<path d="M4.3 5V-8" stroke="#d8bd87" stroke-width=".6"/>' +
      '<path d="M4.3-11L6-8.2L4.3-5.9L2.7-8.2Z" fill="#9de5df"/>' +
      '<path d="M4.3-11L6-8.2L4.3-5.9Z" fill="#4b9bbb"/>';
  } else {
    out += HELMET;
    if (arm === 'shot') {
      out += '<path d="M3.6-7L6.3-4L6.7-.7L5.7 2.5L3.6 4.5L4.9-.8Z" fill="#d7b87a"/>' +
        '<path d="M3.6-7L4.9-.8L3.6 4.5M1-1L8.1-2.5" fill="none" stroke="#eee1bc" stroke-width=".28"/>' +
        '<path d="M8.1-2.5L6.7-3L7.1-1.6Z" fill="#d9e6df"/>';
    } else {
      out += '<path d="M4.2 5L4.2-9.9" stroke="#c6a975" stroke-width=".5"/>' +
        '<path d="M4.2-12.3L5.3-9.7L4.2-8.9L3.2-9.7Z" fill="#d7e2d9"/>' +
        '<path d="M4.2-12.3L5.3-9.7L4.2-8.9Z" fill="#7e99ac"/>' +
        '<path d="M-4.9-3.3L-1.1-2.6L-1.4 1L-3.6 2.4L-5.3.3Z" fill="currentColor" stroke="#d2c3a0" stroke-width=".35"/>' +
        '<path d="M-4.9-3.3L-3.4-3L-3.6 2.4L-5.3.3Z" fill="#ffffff40"/>';
    }
  }
  return out;
}

function horse(): string {
  return '<path d="M-5.8-2L-3.6-4.1L1.8-3.6L3.3-7.6L5-8.3L7.2-6.5L6.9-4.8L4.8-4.9L3.9.9L-4.3 1.4Z" fill="#b49670"/>' +
    '<path d="M-5.8-2L-3.6-4.1L1.8-3.6L.3-1.1Z" fill="#d5bf92"/>' +
    '<path d="M.3-1.1L3.3-7.6L4.8-4.9L3.9.9L-4.3 1.4L-5.8-2Z" fill="#806f5c"/>' +
    '<path d="M-4.3.3L-2.8.3L-3.3 5.4L-5.1 5.4ZM1.8.2L3.6-.3L4.6 5.4L2.8 5.4Z" fill="#635d55"/>' +
    '<path d="M-3.7-3.1L.3-3.9L2.2-.7L-.4 1.6L-3.8.8Z" fill="currentColor"/>' +
    `<g transform="translate(-.6 -6.4) scale(.54)">${soldier('foot')}</g>`;
}

function engine(): string {
  return '<path d="M-6 2.2L-2.3-.2L5.2 1.4L1.4 4Z" fill="#a8875b"/>' +
    '<path d="M-6 2.2L1.4 4V5L-6 3.3ZM1.4 4L5.2 1.4V2.7L1.4 5Z" fill="#61533f"/>' +
    '<path d="M-3.7 1.8L-.6-6.6L.3-5.3L-1.1 2.5ZM3.6 2L.3-5.3L1.4-5.8L5 1.3Z" fill="#ccb18a"/>' +
    '<path d="M-3.5-3.2L4.5-10.1L5.4-9L-2.7-2.2Z" fill="#96724d"/>' +
    '<path d="M3.1-9.1L5.3-10.6L7-9.7L6.3-8L4.9-7.7Z" fill="#a0b2ba"/>' +
    '<path d="M-4.8-3.3L-2.5-4.5L-1.9-1.9L-3.9-1.1Z" fill="#607682"/>' +
    '<path d="M-4.8-3.3L-2.5-4.5L-1.9-1.9L-3.1-2.1Z" fill="#c0c9c3"/>' +
    [-4.2, 2].map((x) => `<path d="M${x - 1.4} 3L${x - 0.9} 1.8L${x + 0.5} 1.4L${x + 1.3} 2.5L${x + 0.9} 4.4L${x - 0.5} 4.9L${x - 1.4} 4.1Z" fill="#344652" stroke="#b2a585" stroke-width=".5"/>`).join('');
}

function army(arm: MapArm): string {
  const figure = arm === 'horse' ? horse() : arm === 'engine' ? engine() : soldier(arm);
  return '<path d="M-5.6 5.1L-2.4 4.1L3.7 4.5L7.4 6.1L4.8 7.1L-.7 6.7L-4.8 5.8Z" fill="#15221d70"/>' +
    `<g stroke="#27362e" stroke-width=".16" stroke-linejoin="round">${figure}</g>`;
}

const SETTLEMENTS = [0, 1, 2, 3].map(settlement);
const ARMS: MapArm[] = ['foot', 'horse', 'shot', 'engine', 'caster'];

/** Muted olive canopies with sunlit northwest facets and long ground shadows. */
function canopy(kind: number): string {
  const trunk = '<path d="M-.1.38L-.07-.59L.12-.62L.15.4Z" fill="#726c50"/>';
  if (kind === 1 || kind === 3) {
    const lit = kind === 3 ? '#8b8966' : '#858566';
    const mid = kind === 3 ? '#65694d' : '#626d50';
    return trunk + '<path d="M-.91-.33L-.78-1.03L-.4-1.32L-.19-1.73L.24-1.76L.73-1.25L.72-.96L.95-.61L.61-.03L.15.12L-.34.02Z" fill="#354536"/>' +
      `<path d="M-.91-.33L-.78-1.03L-.4-1.32L-.19-1.73L.24-1.76L-.07-.78Z" fill="${lit}"/>` +
      `<path d="M.24-1.76L.73-1.25L.72-.96L.95-.61L-.07-.78Z" fill="${mid}"/>` +
      '<path d="M-.91-.33L-.07-.78L.15.12L-.34.02Z" fill="#697356"/>' +
      '<path d="M-.07-.78L.95-.61L.61-.03L.15.12Z" fill="#414f39"/>';
  }
  const narrow = kind === 2 ? 'scale(.69 1.13)' : 'scale(1 1)';
  return trunk + `<g transform="${narrow}">` +
    '<path d="M-.03-1.47L-.77-.13L-.39-.11L-.88.19L.03.46L.82.18L.58-.03L.78-.09Z" fill="#48553c"/>' +
    '<path d="M-.03-1.47L-.77-.13L-.39-.11L-.88.19L.03.46Z" fill="#7b8060"/>' +
    '<path d="M-.03-1.47L.78-.09L.58-.03L.82.18L.03.46Z" fill="#334431"/>' +
    '<path d="M.05-2.17L-.57-.84L-.35-.86L-.67-.55L.02-.29L.62-.59L.41-.88L.53-.84Z" fill="#586748"/>' +
    '<path d="M.05-2.17L-.57-.84L-.35-.86L-.67-.55L.02-.29Z" fill="#8c8d69"/>' +
    '<path d="M.05-2.17L.53-.84L.41-.88L.62-.59L.02-.29Z" fill="#3a4a34"/></g>';
}

/** A close-packed woodland patch, cached once rather than one SVG node per tree. */
function tree(variant: number): string {
  const spots = [
    [-1.18, -0.57, 0.43], [-0.58, -0.77, 0.5], [0.07, -0.53, 0.39],
    [0.74, -0.66, 0.47], [1.37, -0.29, 0.37],
    [-1.57, 0.07, 0.42], [-0.92, -0.03, 0.5], [-0.18, 0.1, 0.55],
    [0.53, 0.04, 0.47], [1.27, 0.35, 0.44],
    [-1.28, 0.72, 0.4], [-0.63, 0.65, 0.47], [0.09, 0.88, 0.5],
    [0.81, 0.76, 0.53], [1.41, 1.1, 0.35],
  ];
  const crowns = spots.map(([x, y, s], i) => {
    // Uneven rows overlap without repeating the same four tall spires. Each
    // definition mixes oak, fir and scrub in a slightly different proportion.
    const kind = variant === 1 ? (i % 5 === 0 ? 0 : i % 2 ? 1 : 3)
      : variant === 2 ? (i % 4 === 0 ? 1 : i % 3 === 0 ? 0 : 2)
        : variant === 3 ? (i % 4 === 0 ? 2 : i % 2 ? 3 : 1)
          : (i + Math.floor(i / 3)) % 4;
    return [x + (((i * 7 + variant * 3) % 5) - 2) * 0.04,
      y + (((i * 3 + variant * 2) % 5) - 2) * 0.035,
      s * (variant === 3 ? 0.92 : 1), kind];
  }).sort((a, b) => a[1] - b[1]);
  let out = '<path d="M-1.96.12L-1.71-.48L-.95-.74L-.17-.62L.59-.76L1.42-.31L1.88.39L2.04.92L1.67 1.46L.97 1.63L.11 1.44L-.73 1.46L-1.5 1.11L-1.81.63Z" fill="#1a281f6e"/>' +
    '<path d="M-1.68.07L-1.39-.46L-.62-.62L.17-.4L.68-.56L1.42-.05L1.74.55L1.53 1.12L.81 1.31L.06 1.18L-.82 1.19L-1.53.79Z" fill="#3a49326e"/>';
  for (const [x, y, s, kind] of crowns) {
    out += `<g transform="translate(${x} ${y}) scale(${s})">${canopy(kind)}</g>`;
  }
  return out;
}

const MARSH = '<path d="M-1.45.3L-.32-.12L1.4.29L.48.78L-.72.78Z" fill="#4e66614d"/>' +
  '<path d="M-1.45.3L.07.4L.48.78L-.72.78Z" fill="#87907836"/>' +
  '<path d="M-.55.31L-.65-.46M-.47.32L-.32-.67M-.4.32L-.12-.21M.64.35L.47-.23M.68.36L.79-.49" fill="none" stroke="#888c62" stroke-width=".07"/>' +
  '<path d="M-.65-.47L-.7-.66M-.32-.67L-.33-.88M.79-.49L.83-.65" stroke="#a39a6c" stroke-width=".11"/>';

const DEFINITIONS = SETTLEMENTS.map((body, i) => `<g id="wm-poly-hold-${i}">${body}</g>`).join('') +
  `<g id="wm-poly-hut" stroke="#30392e" stroke-width=".025">${house(-0.5, -0.5)}</g>` +
  [0, 1, 2, 3].map((v) => `<g id="wm-poly-tree-${v}" opacity=".96">${tree(v)}</g>`).join('') +
  `<g id="wm-poly-marsh">${MARSH}</g>` +
  ARMS.map((arm) => `<g id="wm-poly-arm-${arm}">${army(arm)}</g>`).join('');

export function mapLowPolyDefs(): string { return DEFINITIONS + VILLAGE_DEFS; }

export function lowPolyArmy(arm: MapArm, accent: string): string {
  return `<use href="#wm-poly-arm-${arm}" color="${accent}"/>`;
}

export function lowPolyHoldingPreview(fort: number): string {
  return `<svg class="wm-groundpic" viewBox="-4.4 -4.5 8.8 7.6" aria-hidden="true" color="#d7b775">${
    SETTLEMENTS[Math.max(0, Math.min(3, Math.floor(fort)))]}</svg>`;
}
